/**
 * The answers of /events/search, one snapshot per query string, read while a list shows them.
 * A newest-first query is a feed: the refresh reads its newest page, and an older page goes on
 * from the oldest row held. A ranked query (`searchText`) is one page with no cursor.
 *
 * An enrollment write changes the verdicts on the rows. An enroll names its events, so the held
 * rows take its verdict at once. A re-identification changes verdicts that the app cannot know,
 * and it can move a row out of the scope of a feed, so the feed drops its rows and reads its
 * scope again. Only an object gets a verdict, so a feed of events, such as the events list, keeps
 * its rows; an empty feed is read again, because it can gain sightings. The other changes leave
 * the rows as they are.
 */

import type { Atom, WritableAtom } from 'jotai';

import { queryAtoms } from '../queryAtoms';
import { api } from '@/api';
import { findFirstOfEachId } from '@/lib/firstOfEachId';
import { type EnrollmentChange, EnrollmentChangeKind } from '@/types/enrollments';
import type {
    EventSearchQuery, EventSearchResult, EventSearchSnapshot,
} from '@/types/events';
import {
    ApplyOutcome, type ApplyResult, type OlderState, type QueryWrite,
} from '@/types/snapshot';

/** The cap of a feed: past it, a narrower scope reaches the older events. */
export const MAX_FEED_EVENTS = 1000;

// The page size when the query names none, as the endpoint's own default.
const DEFAULT_PAGE_LIMIT = 100;

interface EventPage {
    rows: EventSearchResult[];
    isOlder: boolean;
    isRanked: boolean;
    /** The page holds `limit` rows, so an older page can follow. */
    isFull: boolean;
}

function isRanked(params: URLSearchParams): boolean {
    return (params.get('searchText') ?? '').trim() !== '';
}

async function readPage(params: URLSearchParams, isOlder: boolean): Promise<EventPage> {
    const rows = await api.searchEvents(params.toString());
    const limit = Number(params.get('limit') ?? DEFAULT_PAGE_LIMIT);
    return { rows, isOlder, isRanked: isRanked(params), isFull: rows.length >= limit };
}

/**
 * Merges a page into the held rows by id, newest first. A head keeps the held rows that it does
 * not have, so a row stays when the head moves forward. The time cursor of the server is
 * inclusive, so the oldest held row comes back, and the merge drops it by id. On an id that both
 * have, `incomingItems` wins, so a poll replaces an old copy of a row.
 */
export function mergeEventsDesc<T extends EventSearchResult>(base: T[], incomingItems: T[]): T[] {
    const byId = new Map<string, T>();
    for (const entry of incomingItems) {
        byId.set(entry.id, entry);
    }
    const mergedItems = [...byId.values()];
    for (const entry of base) {
        if (!byId.has(entry.id)) { mergedItems.push(entry); }
    }
    mergedItems.sort((a, b): number => b.utcTimeMs - a.utcTimeMs);
    return mergedItems;
}

/** Puts a page into the held feed. Exported for its test. */
export function mergeEventPage(
    previousSnapshot: EventSearchSnapshot | null, page: EventPage,
): EventSearchSnapshot {
    const olderPageCount = (previousSnapshot?.olderPageCount ?? 0) + (page.isOlder ? 1 : 0);
    // A failed first load holds the pending snapshot with its error, so a later page is the
    // first page too.
    if (previousSnapshot === null || !previousSnapshot.isLoaded || page.isRanked) {
        // A ranked page keeps the server's order: best first.
        const events = page.isRanked
            ? findFirstOfEachId(page.rows)
            : mergeEventsDesc([], page.rows).slice(0, MAX_FEED_EVENTS);
        return {
            events,
            isLoaded: true,
            hasOlder: !page.isRanked && page.isFull && events.length < MAX_FEED_EVENTS,
            olderPageCount,
            isLoading: false,
            error: null,
        };
    }
    const knownIds = new Set(previousSnapshot.events.map((event) => event.id));
    const isFresh = page.rows.some((event) => !knownIds.has(event.id));
    const events = mergeEventsDesc(previousSnapshot.events, page.rows).slice(0, MAX_FEED_EVENTS);
    const hasOlder = page.isOlder
        ? isFresh && page.isFull && events.length < MAX_FEED_EVENTS
        : previousSnapshot.hasOlder && events.length < MAX_FEED_EVENTS;
    return { events, isLoaded: true, hasOlder, olderPageCount, isLoading: false, error: null };
}

// The category family of the detections that an enrollment matches.
const OBJECT_CATEGORY = 'object';

function isObjectRow(row: EventSearchResult): boolean {
    return row.category.startsWith(OBJECT_CATEGORY);
}

/** What an enrollment change does to the rows of a search. */
export function applyEnrollmentChange(
    previousSnapshot: EventSearchSnapshot, change: EnrollmentChange,
): ApplyResult<EventSearchSnapshot> {
    switch (change.kind) {
        case EnrollmentChangeKind.Enrolled: {
            const ids = new Set(change.eventIds);
            if (!previousSnapshot.events.some((event) => ids.has(event.id))) {
                return { outcome: ApplyOutcome.Patched, snapshot: previousSnapshot };
            }
            const { enrollmentId, isNegative } = change;
            const events = previousSnapshot.events.map((event) => (ids.has(event.id)
                ? { ...event, enrollmentId, isEnrollmentNegative: isNegative }
                : event));
            return { outcome: ApplyOutcome.Patched, snapshot: { ...previousSnapshot, events } };
        }
        case EnrollmentChangeKind.Reidentified:
            return previousSnapshot.events.length === 0 ||
                previousSnapshot.events.some(isObjectRow)
                ? { outcome: ApplyOutcome.Reset }
                : { outcome: ApplyOutcome.Patched, snapshot: previousSnapshot };
        default:
            return { outcome: ApplyOutcome.Patched, snapshot: previousSnapshot };
    }
}

const feeds = queryAtoms<EventSearchQuery, EventSearchSnapshot, EventPage, EnrollmentChange>({
    getRequestKey: ({ params }): string => params,
    getPending: (): EventSearchSnapshot => ({
        events: [], isLoaded: false, hasOlder: false, olderPageCount: 0, isLoading: true,
        error: null,
    }),
    load: async ({ params }): Promise<EventPage> => readPage(new URLSearchParams(params), false),
    merge: mergeEventPage,
    // The server's bound is inclusive, so the oldest row comes back and the merge drops it.
    loadOlder: async ({ params }, previousSnapshot): Promise<EventPage> => {
        const oldestEvent = previousSnapshot.events.at(-1);
        const query = new URLSearchParams(params);
        if (oldestEvent !== undefined) {
            query.set('utcEndTimeMs', String(oldestEvent.utcTimeMs));
        }
        return readPage(query, true);
    },
    apply: applyEnrollmentChange,
});

/** The feed of `query`; write `LoadOlder` for its next page, `Reload` to read it again. */
export function eventSearchAtom(
    query: EventSearchQuery,
): WritableAtom<EventSearchSnapshot, [QueryWrite<EventSearchSnapshot, EnrollmentChange>], void> {
    return feeds.getMember(query);
}

/** The feed of `query`, read again every `intervalMs` or faster while mounted. */
export function liveEventSearchAtom(
    query: EventSearchQuery, intervalMs: number,
): Atom<EventSearchSnapshot> {
    return feeds.getLive(query, intervalMs);
}

/** The state of the older page of `query`; its reader also keeps the feed. */
export function eventSearchOlderStateAtom(query: EventSearchQuery): Atom<OlderState> {
    return feeds.getOlderState(query);
}

/** An enrollment editor's change, sent to each held feed. */
export const applyEnrollmentChangeToSearchesAtom = feeds.applyAll;
